/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import React, { useState } from "react";
import { useRecoilValue } from "recoil";
import { NoteCategoriesAtom } from "../../atoms/NotesAtom";
import useClickOutside from "../../hook/useClickOutside";
import { INoteCategory, INoteType } from "../../types/NoteCategory";
import { DateFilter, FilterNote } from "../Contacts/DetailsScreens/Notes";
import IconBerserkerMail from "../Icon";
import StyledButton from "../StyledButton";

const Popover = styled.div`
  width: 200px;
  max-height: 200px;
  height: fit-content;

  padding: 10px 0px;

  display: flex;
  flex-direction: column;

  position: absolute;
  right: 170px;
  top: 10px;
  z-index: 999;

  background-color: #fff;
  box-shadow: 0px 3px 6px #00000026;

  border: 1px solid;
  border-bottom: none;
  border-top: none;
  border-radius: 5px;

  overflow-y: auto;

  ::-webkit-scrollbar {
    display: block;
  }
`;

interface ButtonFilterProps {
  selected: boolean;
}

const StyledButtonWithIcon = styled(StyledButton)<ButtonFilterProps>`
  height: 40px;

  padding: 8px 16px;

  display: flex;
  align-items: center;
  justify-content: center;

  position: relative;

  ${(props) =>
    props.selected &&
    css`
      border-color: #fb972e;
    `}

  .close {
    margin-left: 16px;
  }

  .number,
  .icon-filter {
    margin-right: 8px;
  }

  :hover {
    opacity: 1 !important;
  }
`;

const TitlePopover = styled.div`
  font-size: 12px;
  color: #888888;
  padding: 10px;
  margin-left: 7px;
  font-weight: 400;
`;

const AddContactPopover = styled.div`
  position: absolute;
  background: white;
  z-index: 20;
  top: 42px;
  height: fit-content;
  max-height: 200px;
  width: 180px;
  border-radius: 5px;
  box-shadow: 0px 3px 20px #00000026;
  border: 1px solid #d4d4d4;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 10px 0px;
  right: -10px;
`;

const GroupName = styled.div`
  display: flex;
  align-items: center;
  color: #888888;
  font-weight: 400;
  margin-left: 7px;
  padding: 10px;
  font-size: 12px;
`;

const Number = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  background-color: #fb972e;
  color: #fff;
  width: 20px;
  height: 20px;
  font-size: 12px;
  border-radius: 100%;
  font-weight: 600;
  line-height: 20px;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  padding: 10px;
  font-size: 15px;
  color: #2f2f2f;
  cursor: pointer;
  position: relative;
  width: 100%;
  justify-content: space-between;
  font-weight: 400;

  span {
    margin-left: 7px;
  }

  &:hover {
    background-color: #fb972e;
    color: #ffffff;
  }
`;

const categoriCss = css`
  font-weight: 400;
  :hover {
    svg {
      path {
        fill: #ffffff;
        stroke: #ffffff;
      }
    }
  }
`;

interface Props {
  filter: FilterNote;
  handleChangeFilter: (vle: FilterNote) => void;
}

export default function FilterNotes(props: Props) {
  const { filter, handleChangeFilter } = props;
  const wrapperRef = useClickOutside(() => setOpenDropView(false));
  const [openDropView, setOpenDropView] = useState(false);
  const categories = useRecoilValue(NoteCategoriesAtom);

  const [showCategory, setShowCategory] = useState(false);
  const [showDate, setShowDate] = useState(false);
  const [showType, setShowType] = useState(false);

  function selectCategory(c: INoteCategory) {
    if (filter.categories.find((cat) => cat._id === c._id)) {
      const newCategories = filter.categories.filter((cc) => cc._id !== c._id);
      handleChangeFilter({ ...filter, categories: newCategories });
    } else {
      handleChangeFilter({ ...filter, categories: [...filter.categories, c] });
    }
  }

  function selectType(t: INoteType) {
    if (filter.type.find((type) => type === t)) {
      const newTypes = filter.type.filter((tt) => tt !== t);
      handleChangeFilter({ ...filter, type: newTypes });
    } else {
      handleChangeFilter({ ...filter, type: [...filter.type, t] });
    }
  }

  return (
    <StyledButtonWithIcon
      selected={filter.categories.length > 0 || filter.type.length > 0}
      inverted
      css={css`
        margin-right: 0;
      `}
      onClick={(e) => {
        setOpenDropView(true);
        e.stopPropagation();
      }}
    >
      {filter.categories.length > 0 || filter.type.length > 0 ? (
        <Number className="number">
          {filter.categories.length + filter.type.length}
        </Number>
      ) : (
        <div className="icon-filter">
          <IconBerserkerMail name="filter" />
        </div>
      )}
      Filter
      {(filter.categories.length > 0 || filter.type.length > 0) && (
        <div
          className="close"
          onClick={(e) => {
            handleChangeFilter({ ...filter, categories: [], type: [] });
            setOpenDropView(false);
            e.stopPropagation();
          }}
        >
          <IconBerserkerMail
            name="close-modal"
            color="#bbb"
            width={12}
            height={12}
          />
        </div>
      )}
      {openDropView && (
        <AddContactPopover ref={wrapperRef}>
          <TitlePopover>FILTERS</TitlePopover>

          <Row
            onMouseOver={() => setShowDate(true)}
            onMouseLeave={() => setShowDate(false)}
          >
            <span>By Date</span>

            {showDate && (
              <Popover>
                <GroupName>DATE</GroupName>

                <Row
                  css={categoriCss}
                  onClick={() => {
                    handleChangeFilter({ ...filter, date: DateFilter.Recent });
                    setOpenDropView(false);
                  }}
                >
                  <span>Most Recent</span>
                  {filter.date === DateFilter.Recent && (
                    <IconBerserkerMail
                      name="check"
                      color="#fb972e"
                      width={12}
                      height={10}
                    />
                  )}
                </Row>
                <Row
                  css={categoriCss}
                  onClick={() => {
                    handleChangeFilter({ ...filter, date: DateFilter.Older });
                    setOpenDropView(false);
                  }}
                >
                  <span>Older</span>

                  {filter.date === DateFilter.Older && (
                    <IconBerserkerMail
                      name="check"
                      color="#fb972e"
                      width={12}
                      height={10}
                    />
                  )}
                </Row>
              </Popover>
            )}
          </Row>

          <Row
            onMouseOver={() => setShowType(true)}
            onMouseLeave={() => setShowType(false)}
          >
            <span>By Type</span>

            {showType && (
              <Popover>
                <GroupName>TYPE</GroupName>

                {[INoteType.SYSTEM, INoteType.USER].map((t) => {
                  return (
                    <Row css={categoriCss} onClick={() => selectType(t)}>
                      <span>
                        {t === INoteType.SYSTEM ? "System Notes" : "User Notes"}
                      </span>

                      {filter.type.find((typ) => typ === t) && (
                        <IconBerserkerMail
                          name="check"
                          color="#fb972e"
                          width={12}
                          height={10}
                        />
                      )}
                    </Row>
                  );
                })}
              </Popover>
            )}
          </Row>

          <Row
            onMouseOver={() => setShowCategory(true)}
            onMouseLeave={() => setShowCategory(false)}
          >
            <span>By Category</span>

            {showCategory && (
              <Popover>
                {[INoteType.SYSTEM, INoteType.USER].map((e) => {
                  if (categories.find((c) => c.type === e))
                    return (
                      <>
                        <GroupName>
                          {e === INoteType.SYSTEM ? "SYSTEM" : "USER"}
                        </GroupName>

                        {categories
                          .filter((cc) => cc.type === e)
                          .sort((a, b) =>
                            a.name.localeCompare(b.name, undefined, {
                              sensitivity: "base",
                            })
                          )
                          .map((c) => {
                            return (
                              <Row
                                css={categoriCss}
                                onClick={() => selectCategory(c)}
                              >
                                <span>{c.name}</span>

                                {filter.categories.find(
                                  (cat) => cat._id === c._id
                                ) && (
                                  <IconBerserkerMail
                                    name="check"
                                    color="#fb972e"
                                    width={12}
                                    height={10}
                                  />
                                )}
                              </Row>
                            );
                          })}
                      </>
                    );
                })}
              </Popover>
            )}
          </Row>
        </AddContactPopover>
      )}
    </StyledButtonWithIcon>
  );
}
